import { ICalendarSource } from '../../data/entities/calendar-source';
import { idOrLegacyId } from '../../entities/properties/selectors';
import { ActionTypeNames, ActionValues, createActionCreators } from '../../store/actions';
import { ThunkAction } from '../../store/middleware';

export const getCalendarSourcesByPropertyIdActions = createActionCreators<
	{ T: 'CALENDAR_SOURCE_GET_BY_PROP_ID_REQUEST', P: { propertyId: string | number } },
	{ T: 'CALENDAR_SOURCE_GET_BY_PROP_ID_RECEIVE', P: { propertyId: string | number, calendarSources: ICalendarSource[] } },
	{ T: 'CALENDAR_SOURCE_GET_BY_PROP_ID_FAILURE', P: { error: Error } }
>('CALENDAR_SOURCE_GET_BY_PROP_ID_REQUEST', 'CALENDAR_SOURCE_GET_BY_PROP_ID_RECEIVE', 'CALENDAR_SOURCE_GET_BY_PROP_ID_FAILURE');

export const getCalendarSourcesByPropertyId = (id: string | number): ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
	const propertyId = idOrLegacyId(id);
	dispatch(getCalendarSourcesByPropertyIdActions.CALENDAR_SOURCE_GET_BY_PROP_ID_REQUEST({ propertyId }));
	try {
		const calendarSources = await api.calendarSource.getCalendarSources(propertyId);
		dispatch(getCalendarSourcesByPropertyIdActions.CALENDAR_SOURCE_GET_BY_PROP_ID_RECEIVE({ propertyId, calendarSources }));
	} catch (error) {
		dispatch(getCalendarSourcesByPropertyIdActions.CALENDAR_SOURCE_GET_BY_PROP_ID_FAILURE({ error }));
		throw error;
	}
};

export const getCalendarSourceIcalsByPropertyIdActions = createActionCreators<
	{ T: 'CALENDAR_SOURCE_ICALS_GET_BY_PROP_ID_REQUEST', P: { propertyId: string | number } },
	{ T: 'CALENDAR_SOURCE_ICALS_GET_BY_PROP_ID_RECEIVE', P: { propertyId: string | number, calendarSources: ICalendarSource[] } },
	{ T: 'CALENDAR_SOURCE_ICALS_GET_BY_PROP_ID_FAILURE', P: { error: Error } }
>('CALENDAR_SOURCE_ICALS_GET_BY_PROP_ID_REQUEST', 'CALENDAR_SOURCE_ICALS_GET_BY_PROP_ID_RECEIVE', 'CALENDAR_SOURCE_ICALS_GET_BY_PROP_ID_FAILURE');

export const getCalendarSourceIcalsByPropertyId = (id: string | number): ThunkAction<any> => async (dispatch, getState, { _deprecatedApi: api }) => {
	const propertyId = idOrLegacyId(id);
	dispatch(getCalendarSourceIcalsByPropertyIdActions.CALENDAR_SOURCE_ICALS_GET_BY_PROP_ID_REQUEST({ propertyId }));
	try {
		const calendarSources = await api.calendarSource.getIcalSources(propertyId);
		dispatch(getCalendarSourceIcalsByPropertyIdActions.CALENDAR_SOURCE_ICALS_GET_BY_PROP_ID_RECEIVE({ propertyId, calendarSources }));
	} catch (error) {
		dispatch(getCalendarSourceIcalsByPropertyIdActions.CALENDAR_SOURCE_ICALS_GET_BY_PROP_ID_FAILURE({ error }));
		throw error;
	}
};

export const createCalendarSourceActions = createActionCreators<
	{ T: 'CALENDAR_SOURCE_CREATE_REQUEST', P: { propertyId: string | number, calendarSource: ICalendarSource } },
	{ T: 'CALENDAR_SOURCE_CREATE_RECEIVE', P: { propertyId: string | number, calendarSource: ICalendarSource } },
	{ T: 'CALENDAR_SOURCE_CREATE_FAILURE', P: { error: Error } }
>('CALENDAR_SOURCE_CREATE_REQUEST', 'CALENDAR_SOURCE_CREATE_RECEIVE', 'CALENDAR_SOURCE_CREATE_FAILURE');

export const createCalendarSource = (id: string | number, calendarSource: ICalendarSource): ThunkAction<any> =>
	async (dispatch, getState, { _deprecatedApi: api }) => {
		const propertyId = idOrLegacyId(id);
		dispatch(createCalendarSourceActions.CALENDAR_SOURCE_CREATE_REQUEST({ propertyId, calendarSource }));
		try {
			const returnedSource = await api.calendarSource.createCalendarSource(propertyId, calendarSource);
			dispatch(createCalendarSourceActions.CALENDAR_SOURCE_CREATE_RECEIVE({ propertyId, calendarSource: returnedSource }));
		} catch (error) {
			dispatch(createCalendarSourceActions.CALENDAR_SOURCE_CREATE_FAILURE({ error }));
			throw error;
		}
};

export const deleteCalendarSourceByIdActions = createActionCreators<
	{ T: 'CALENDAR_SOURCE_DELETE_BY_ID_REQUEST', P: { propertyId: string | number, calendarSourceId: number } },
	{ T: 'CALENDAR_SOURCE_DELETE_BY_ID_RECEIVE', P: ICalendarSource },
	{ T: 'CALENDAR_SOURCE_DELETE_BY_ID_FAILURE', P: { error: Error } }
>('CALENDAR_SOURCE_DELETE_BY_ID_REQUEST', 'CALENDAR_SOURCE_DELETE_BY_ID_RECEIVE', 'CALENDAR_SOURCE_DELETE_BY_ID_FAILURE');

export const deleteCalendarSourceById = (id: string | number, calendarSourceId: number): ThunkAction<any> =>
	async (dispatch, getState, { _deprecatedApi: api }) => {
		const propertyId = idOrLegacyId(id);
		dispatch(deleteCalendarSourceByIdActions.CALENDAR_SOURCE_DELETE_BY_ID_REQUEST({ propertyId, calendarSourceId }));
		try {
			const returnedSource = await api.calendarSource.deleteCalendarSource(propertyId, calendarSourceId);
			dispatch(getCalendarSourceIcalsByPropertyId(propertyId));
			dispatch(deleteCalendarSourceByIdActions.CALENDAR_SOURCE_DELETE_BY_ID_RECEIVE(returnedSource));
		} catch (error) {
			dispatch(deleteCalendarSourceByIdActions.CALENDAR_SOURCE_DELETE_BY_ID_FAILURE({ error }));
			throw error;
		}
};

const actions = {
	...getCalendarSourcesByPropertyIdActions,
	...getCalendarSourceIcalsByPropertyIdActions,
	...createCalendarSourceActions,
	...deleteCalendarSourceByIdActions
};

type CalendarSourceActions = ActionValues<typeof actions>;
type CalendarSourceActionTypes = ActionTypeNames<CalendarSourceActions>;
export type Actions = CalendarSourceActions[keyof CalendarSourceActions];
export const ActionTypes = Object.keys(actions).reduce((types, t) => {
	types[t] = t;
	return types;
}, {} as CalendarSourceActionTypes);
